/**
 * THE MOMENTS - short sounds, rendered once at unlock, fired from a buffer afterwards.
 *
 * -- WHAT THE GAME ACTUALLY HAS MOMENTS OF, WHICH IS NOT WHAT YOU WOULD GUESS ----
 *
 * A fishing game suggests three beats: the cast, the bite, the landing. This simulation has
 * none of them as separate events, and pretending otherwise would have put a sound where there
 * is no gesture. What it has is a cast that resolves every `castTicks` seconds - four for a
 * minnow, eleven for a cod, halved again at full skill - into one of three outcomes, with no
 * player act anywhere in the loop and no instant that could be called a bite. The cast does not
 * start, it continues; the previous landing and the next cast are the same tick.
 * `cadence.test.ts` measures the resulting rate against the live engine and prints it.
 *
 * So the moments here are the ones the build genuinely has:
 *
 *   - A LANDING, which is frequent and must therefore be small.
 *   - A HEAVY landing, which is the same gesture with more of it, chosen continuously by where
 *     the fish falls in its own species range.
 *   - A PERSONAL BEST, which is rare and is allowed to be a sound.
 *   - JUNK, which is not a fish.
 *   - THE SHEET LIFTING AND SETTLING, which already has a gesture on screen and no voice.
 *   - A MARK, when the player uses the one verb the contract gives them.
 *
 * -- WHY THEY ARE BAKED RATHER THAN PLAYED LIVE ----------------------------------
 *
 * Firing a one-shot costs a buffer source and a gain that disconnect themselves when the sound
 * ends. Synthesising the same thing live would cost an oscillator, a filter and two envelopes
 * per event, and the idle graph would have to keep the machinery for it. Baking also makes the
 * output measurable offline, which is the only way this lane can make a claim about a sound.
 *
 * -- EVERY BUFFER ENDS AT ZERO, AND THAT IS ENFORCED -----------------------------
 *
 * A one-shot whose last sample is not zero clicks when it stops, which is the same defect as a
 * loop seam wearing different clothes. `finish` applies a raised-cosine fade at both ends and
 * `moments.test.ts` asserts the endpoints are exactly zero on every buffer in the bank.
 */

import { highpassLinear, lowpassLinear } from './filters';
import { makeRng } from './rng';
import { MOMENT_PEAK, SEEDS } from './spec';

/** Milliseconds of raised-cosine at each end. Long enough to kill a step, short enough to keep an attack. */
const EDGE_MS = 1.5;

/** Fade both ends to exactly zero and scale to a peak. Returns the buffer for chaining. */
function finish(buf: Float32Array, sampleRate: number, peak: number): Float32Array {
  const edge = Math.max(2, Math.round((EDGE_MS / 1000) * sampleRate));
  const n = buf.length;
  for (let i = 0; i < Math.min(edge, n); i++) {
    const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / edge);
    buf[i] = (buf[i] as number) * w;
    buf[n - 1 - i] = (buf[n - 1 - i] as number) * w;
  }
  let max = 0;
  for (let i = 0; i < n; i++) max = Math.max(max, Math.abs(buf[i] as number));
  if (max > 0) {
    const g = peak / max;
    for (let i = 0; i < n; i++) buf[i] = (buf[i] as number) * g;
  }
  // The fade wrote a taper over the first and last samples; force the endpoints exactly to
  // zero so the assertion in the test is about an exact value and not about a tolerance.
  if (n > 0) {
    buf[0] = 0;
    buf[n - 1] = 0;
  }
  return buf;
}

/** exp(-t/tau) sampled at index i. */
const decay = (i: number, sampleRate: number, tau: number): number => Math.exp(-i / (sampleRate * tau));

/** Linear interpolation between two numbers, and the same on a log scale for frequencies. */
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;
const mixLog = (a: number, b: number, t: number): number => Math.exp(mix(Math.log(a), Math.log(b), t));

/**
 * A LANDING, as a function of how heavy the fish was within its own species range.
 *
 * The pitched part is a bubble. A bubble in water oscillates at a frequency set by its radius
 * and RISES as it collapses, with the amplitude falling away exponentially - which is why a
 * water drop has that particular upward flick and why a synthesised "plip" that holds one pitch
 * never sounds like water. Bigger fish displace more water, so `weight` takes the fundamental
 * down, the decay longer, the splash louder and duller, and adds a low body under it that the
 * light end does not have at all.
 *
 * `weight` is 0 for the smallest of its species and 1 for the largest. It is a continuous
 * parameter here; the bank quantises it into buckets, because five renderings and a detune are
 * indistinguishable from a continuum and cost a hundredth of the memory.
 */
export function renderLanding(sampleRate: number, weight: number, seed: number): Float32Array {
  const w = Math.max(0, Math.min(1, weight));
  const seconds = mix(0.19, 0.56, w);
  const n = Math.round(seconds * sampleRate);
  const rng = makeRng(seed);

  const f0 = mixLog(880, 245, w);
  const rise = mix(0.95, 0.42, w);
  const tau = mix(0.055, 0.24, w);
  const splashGain = mix(0.32, 0.72, w);
  const splashHz = mixLog(4600, 2100, w);
  const bodyGain = w * w * 0.55;
  const bodyHz = mixLog(150, 62, w);

  // The break: a short band of noise, the surface being opened. Shaped separately so the
  // splash can be dull on a heavy fish while the bubble above it stays clean.
  const splash = new Float32Array(n);
  for (let i = 0; i < n; i++) splash[i] = rng.bipolar();
  highpassLinear(splash, splashHz * 0.28, sampleRate);
  lowpassLinear(splash, splashHz, sampleRate);
  lowpassLinear(splash, splashHz, sampleRate);

  const out = new Float32Array(n);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const f = f0 * (1 + rise * t);
    phase += (2 * Math.PI * f) / sampleRate;
    const env = decay(i, sampleRate, tau);
    // The splash is front-loaded hard: it is the instant of contact, not a tail.
    const splashEnv = decay(i, sampleRate, tau * 0.35);
    const body = bodyGain > 0 ? Math.sin((2 * Math.PI * bodyHz * i) / sampleRate) * decay(i, sampleRate, tau * 1.6) * bodyGain : 0;
    out[i] = Math.sin(phase) * env + (splash[i] as number) * splashEnv * splashGain + body;
  }
  return finish(out, sampleRate, mix(MOMENT_PEAK.land, MOMENT_PEAK.landHeavy, w));
}

/**
 * NOT A FISH. Short, dull, and deliberately without the upward flick that makes the landing
 * read as something alive: a lump of noise and a low knock, gone in a tenth of a second. It
 * has to be recognisably not-a-landing without being a punishment, because CONTRACT section 6
 * forbids punishment mechanics near the core loop and a sad trombone is one.
 */
export function renderJunk(sampleRate: number, seed: number): Float32Array {
  const n = Math.round(0.15 * sampleRate);
  const rng = makeRng(seed);
  const noise = new Float32Array(n);
  for (let i = 0; i < n; i++) noise[i] = rng.bipolar();
  highpassLinear(noise, 120, sampleRate);
  lowpassLinear(noise, 900, sampleRate);
  lowpassLinear(noise, 900, sampleRate);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const knock = Math.sin((2 * Math.PI * 96 * i) / sampleRate) * decay(i, sampleRate, 0.028) * 0.5;
    out[i] = (noise[i] as number) * decay(i, sampleRate, 0.05) + knock;
  }
  return finish(out, sampleRate, MOMENT_PEAK.junk);
}

/**
 * A PERSONAL BEST. The only sound in the game with a pitch you could name, and it gets one
 * because it is the only event rare enough to deserve it.
 *
 * The partials are INHARMONIC - 1, 2.41, 4.07 - which is what a struck solid does and what a
 * musical instrument does not. A harmonic stack reads as a note, a note implies a key, and a
 * key implies music this game does not have. An inharmonic stack reads as an object being
 * touched, which is the register the rest of the interface is already in.
 */
export function renderRecord(sampleRate: number, seed: number): Float32Array {
  const n = Math.round(0.95 * sampleRate);
  const rng = makeRng(seed);
  const base = 505 * mix(0.97, 1.03, rng.next());
  const partials: readonly [number, number, number][] = [
    [1, 1, 0.72],
    [2.41, 0.34, 0.4],
    [4.07, 0.14, 0.2],
  ];
  const out = new Float32Array(n);
  for (const [ratio, gain, tau] of partials) {
    const f = base * ratio;
    for (let i = 0; i < n; i++) {
      out[i] = (out[i] as number) + Math.sin((2 * Math.PI * f * i) / sampleRate) * decay(i, sampleRate, tau) * gain;
    }
  }
  // A breath of noise at the strike, so it is a thing being touched rather than a tone fading up.
  for (let i = 0; i < Math.round(0.012 * sampleRate); i++) {
    out[i] = (out[i] as number) + rng.bipolar() * 0.25 * decay(i, sampleRate, 0.006);
  }
  return finish(out, sampleRate, MOMENT_PEAK.record);
}

/**
 * PAPER. The sheet already lifts and settles on screen with an ease-out over 260 ms and has
 * never had a voice; this is the voice, and it is the same 260 ms.
 *
 * Two copies of the same noise through different lowpasses, crossfaded across the sound, is how
 * the brightness moves without a filter running at play time: lifting goes from dull to bright
 * because the sheet is coming towards you, settling does the reverse and ends on a small low
 * thud, which is the paper meeting the table.
 */
export function renderPaper(sampleRate: number, seed: number, up: boolean): Float32Array {
  const seconds = up ? 0.26 : 0.2;
  const n = Math.round(seconds * sampleRate);
  const rng = makeRng(seed);
  const src = new Float32Array(n);
  for (let i = 0; i < n; i++) src[i] = rng.bipolar();
  const dull = Float32Array.from(src);
  const bright = Float32Array.from(src);
  highpassLinear(dull, 500, sampleRate);
  lowpassLinear(dull, 1500, sampleRate);
  lowpassLinear(dull, 1500, sampleRate);
  highpassLinear(bright, 1400, sampleRate);
  // TWO POLES, and the measurement is why. One pole at 5,200 Hz is only 6 dB an octave, which
  // left the power centroid of the lift at 7,478 Hz - brighter than paper and into the region
  // that makes a repeated interface sound tiring. Twelve dB an octave brings it back under the
  // top of the paper band without touching the attack, which is what carries the gesture.
  lowpassLinear(bright, 4800, sampleRate);
  lowpassLinear(bright, 4800, sampleRate);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const toBright = up ? t : 1 - t;
    // A sheet of paper is loudest in the middle of the movement, not at the start of it.
    const env = Math.sin(Math.PI * Math.min(1, t * 1.15)) ** 1.4;
    const thud = up ? 0 : Math.sin((2 * Math.PI * 78 * i) / sampleRate) * decay(Math.max(0, i - n * 0.6), sampleRate, 0.03) * (i > n * 0.6 ? 0.45 : 0);
    out[i] = ((dull[i] as number) * (1 - toBright) + (bright[i] as number) * toBright) * env + thud;
  }
  return finish(out, sampleRate, up ? MOMENT_PEAK.lift : MOMENT_PEAK.settle);
}

/**
 * A MARK - graphite on paper, seventy milliseconds of it.
 *
 * This is the sound of the player's one verb (CONTRACT section 6): choosing what to fish for,
 * or choosing where to sail. It is the quietest thing in the bank on purpose. A choice that
 * announces itself is a choice the interface is proud of, and this one is meant to feel like
 * writing a line in your own ledger.
 */
export function renderMark(sampleRate: number, seed: number): Float32Array {
  const n = Math.round(0.072 * sampleRate);
  const rng = makeRng(seed);
  const out = new Float32Array(n);
  // Grain: the noise is itself amplitude-modulated by a slower noise, which is what a scrape is.
  let grain = 0;
  for (let i = 0; i < n; i++) {
    grain = grain * 0.86 + rng.bipolar() * 0.14;
    out[i] = rng.bipolar() * (0.55 + Math.abs(grain) * 3.2);
  }
  highpassLinear(out, 900, sampleRate);
  lowpassLinear(out, 3200, sampleRate);
  lowpassLinear(out, 3200, sampleRate);
  for (let i = 0; i < n; i++) {
    const t = i / n;
    out[i] = (out[i] as number) * Math.sin(Math.PI * t) ** 0.8;
  }
  return finish(out, sampleRate, MOMENT_PEAK.mark);
}

/** The kinds of moment the game can ask for. */
export type MomentKind = 'land' | 'junk' | 'record' | 'lift' | 'settle' | 'mark';

/** How many weight steps the landing is rendered at. Five plus a detune is a continuum by ear. */
export const LAND_BUCKETS = 5;
/** Distinct renderings per bucket, so two of the same fish in a row are not the same sample. */
export const LAND_VARIANTS = 3;
const JUNK_VARIANTS = 2;
const RECORD_VARIANTS = 2;
const MARK_VARIANTS = 3;

/** Everything the engine can play, rendered once. */
export interface MomentBank {
  readonly land: readonly Float32Array[][];
  readonly junk: readonly Float32Array[];
  readonly record: readonly Float32Array[];
  readonly lift: Float32Array;
  readonly settle: Float32Array;
  readonly mark: readonly Float32Array[];
  /** Total samples across the bank, so the memory cost can be reported rather than guessed. */
  readonly samples: number;
}

/** Render the whole bank. Deterministic: same sample rate in, same bytes out. */
export function renderMomentBank(sampleRate: number): MomentBank {
  const s = SEEDS.moments;
  const land: Float32Array[][] = [];
  for (let b = 0; b < LAND_BUCKETS; b++) {
    const row: Float32Array[] = [];
    const weight = b / Math.max(1, LAND_BUCKETS - 1);
    for (let v = 0; v < LAND_VARIANTS; v++) row.push(renderLanding(sampleRate, weight, s + b * 977 + v * 31));
    land.push(row);
  }
  const junk: Float32Array[] = [];
  for (let v = 0; v < JUNK_VARIANTS; v++) junk.push(renderJunk(sampleRate, s + 5000 + v * 41));
  const record: Float32Array[] = [];
  for (let v = 0; v < RECORD_VARIANTS; v++) record.push(renderRecord(sampleRate, s + 6000 + v * 43));
  const mark: Float32Array[] = [];
  for (let v = 0; v < MARK_VARIANTS; v++) mark.push(renderMark(sampleRate, s + 7000 + v * 47));
  const lift = renderPaper(sampleRate, s + 8000, true);
  const settle = renderPaper(sampleRate, s + 8001, false);

  let samples = lift.length + settle.length;
  for (const row of land) for (const b of row) samples += b.length;
  for (const b of junk) samples += b.length;
  for (const b of record) samples += b.length;
  for (const b of mark) samples += b.length;

  return { land, junk, record, lift, settle, mark, samples };
}
